import { SmButton } from '@smiles/smiles-react-design-system';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { HeaderMobile, TimeListItem } from '..';
import { useContextSelector } from '../../hooks';
import { ItineraryData, TabTypes } from '../../types';
import { Utils } from '../../utils';

export interface ITimeModalProps {
  id: string;
  isOpen: boolean;
  onModalClose: () => void;
  onTimeSelect: (time: ItineraryData) => void;
}

export const TimeModal = ({ id, isOpen, onModalClose, onTimeSelect, ...props }: ITimeModalProps) => {
  const {
    search: { location, dates, time },
    liferay: { antecedenceTime },
  } = useContextSelector();
  const [previewTime, setPreviewTime] = useState<ItineraryData>({ pickup: null, dropoff: null });
  const [activeTab, setActiveTab] = useState<TabTypes>(previewTime.pickup ? 'pickup' : 'dropoff');
  const listRef = useRef<HTMLUListElement>(null);

  const timeArray = useMemo(() => {
    const START_TIME = '00:00:00';
    const END_TIME = '23:30:00';

    if (location.pickup && dates.pickup) {
      const isInternational = Utils.generics.isInternationalAgency(location.pickup);
      const isSelectedDateAfterCurrentDay = new Date(dates.pickup) >= new Date();

      if (!isSelectedDateAfterCurrentDay) {
        return Utils.time.generateTimeArray(START_TIME, END_TIME).filter((currentTime) => {
          const antecedence = Utils.generics.getAntecedenceByInternationality(antecedenceTime, isInternational);
          return Utils.time.filterTimeByAntecedenceHours(currentTime, antecedence, new Date());
        });
      }

      const today = new Date();
      const todayMinutes = today.getMinutes();
      const tomorrowDate = new Date();
      tomorrowDate.setDate(today.getDate() + 1);
      tomorrowDate.setHours(0, 0, 0, 0);
      const isTomorrowDate = new Date(dates.pickup).valueOf() === tomorrowDate.valueOf();

      if (isTomorrowDate) {
        // apply the remaining antecedence hours on the next day (tomorrow)
        const todayHours = 24 - new Date().getHours();
        tomorrowDate.setHours(0, todayMinutes, 0, 0);

        return Utils.time.generateTimeArray(START_TIME, END_TIME).filter((currentTime) => {
          const antecedence = Utils.generics.getAntecedenceByInternationality(antecedenceTime, isInternational);
          return Utils.time.filterTimeByAntecedenceHours(currentTime, antecedence - todayHours, tomorrowDate);
        });
      }

      return Utils.time.generateTimeArray(START_TIME, END_TIME);
    }

    return [];
  }, [dates, location, antecedenceTime]);

  // Persist the text values on each modal tab according to the context state
  useLayoutEffect(() => {
    if (time.pickup) {
      setPreviewTime(time);
    } else {
      setPreviewTime({ pickup: null, dropoff: null });
    }
  }, [time]);

  // Set the modal active tab according to which input was clicked (pickup or dropoff inputs)
  useEffect(() => {
    const timeInputs = document.querySelectorAll<HTMLInputElement>('.time-input-field');

    Array.from(timeInputs).forEach((input) => {
      if (input.classList.contains('focused')) {
        const inputType = input.id.split('time-input-field-')[1] as TabTypes;
        setActiveTab(inputType);
      }
    });
  }, [isOpen]);

  // Apply "selected" status to the 'li' that matches the selected time according to the currently active tab
  useEffect(() => {
    const list = listRef.current as HTMLUListElement;
    const activeTabType: TabTypes = activeTab === 'pickup' ? 'pickup' : 'dropoff';

    Array.from(list.children).forEach((item) => {
      const itemButton = item.firstElementChild as HTMLButtonElement;
      const itemTimeText = itemButton.firstElementChild as HTMLSpanElement;

      if (itemTimeText.textContent === previewTime[activeTabType]) {
        item.classList.add('selected');
      } else {
        item.classList.remove('selected');
      }
    });
  }, [activeTab, isOpen]);

  const handleHeaderClose = () => {
    if (!time.pickup) {
      setPreviewTime({ pickup: null, dropoff: null });
    } else {
      setPreviewTime(time);
    }

    onModalClose();
  };

  const handleHeaderTabClick = (tabType: TabTypes) => {
    setActiveTab(tabType);
  };

  const handleItemSelect = (item: string) => {
    if (!previewTime.pickup) {
      setPreviewTime({ pickup: item, dropoff: item });
      setActiveTab('dropoff');
    } else {
      setPreviewTime((prevState) => ({ ...prevState, [activeTab]: item }));
    }
  };

  const handleMobileConfirm = () => {
    if (previewTime.pickup && previewTime.dropoff) {
      onTimeSelect(previewTime);
      onModalClose();
    }
  };

  return (
    <section className={`time-modal-container${isOpen ? ' open' : ''}`} id={`time-select-container-${id}`} {...props}>
      <HeaderMobile.Tabbed
        type="time"
        label={activeTab === 'pickup' ? 'Hora de retirada' : 'Hora de devolução'}
        selectedData={previewTime}
        activeTab={activeTab}
        onTabClick={handleHeaderTabClick}
        onIconClick={handleHeaderClose}
      />

      <ul className="time-modal-list" id={`time-modal-${id}`} ref={listRef}>
        {timeArray.map((timeItem, index) => (
          <TimeListItem
            key={`time-modal-list-item-${index + 1}`}
            text={timeItem}
            type={activeTab}
            onSelect={handleItemSelect}
          />
        ))}
      </ul>

      <section className={`time-modal-mobile-footer${previewTime.pickup ? ' open' : ''}`}>
        <SmButton className="time-modal-mobile-footer-button" text="Confirmar horários" onClick={handleMobileConfirm} />
      </section>
    </section>
  );
};
